// src/components/FeedbackTimelineEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, storage } from '../firebase';
import {
  collection,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { createTimestampedFeedback, deleteTimestampedFeedback, subscribeTimestampedFeedback } from '../utils/videoFeedback';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { deleteObject } from 'firebase/storage';
import { deliverReviewedVideo } from '../utils/reviewMessaging';
import Swal from 'sweetalert2';
import { generateAiReviewDraft, editAiReviewDraft, approveAiReviewDraft, unapproveAiReview, publishAiReview } from '../utils/aiReviewApi';
import AiCopilotPanel from './ai/copilot/AiCopilotPanel';
import { buildClientSignalsFromWorkspace } from '../utils/clientSignals';
import { useAuth } from '../AuthContext';
import {
  getBuiltInDrillTemplatesBySport,
  getCanonicalOrgId,
  groupDrillTemplates,
  matchesDrillFilter,
  normalizeDrillTemplate,
  normalizeSkillReferences,
  normalizeDrillTemplates,
  toReviewDrill,
} from '../utils/drillTemplates';
import { loadAccessibleDrillTemplates } from '../utils/drillTemplateAccess';
import { createDrillTemplate } from '../utils/drillTemplateWrites';
import { getAvailableSkillsForVideo, resolveFocusSkillReferences } from '../utils/skillMapping';
import {
  getAnalysisDisplayMeta,
  getSkiAnalysisLabel,
  getSnowboardAnalysisLabel,
  isSkiVideo,
  SKI_PERFORMANCE_ISSUES,
  SKI_PRIMARY_PERFORMANCE_ISSUES,
  SNOWBOARD_BOARD_PERFORMANCE_ISSUES,
  SNOWBOARD_INEFFICIENCY_TAGS,
  hasMeaningfulAnalysis,
  isSnowboardVideo,
  normalizeReviewAnalysis,
  sanitizeReviewAnalysis,
} from '../utils/reviewAnalysis';

function FeedbackTimelineEditor({ videoId, videoURL }) {
  const { currentUser, userData } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackType, setFeedbackType] = useState('text');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [deletingFeedbackId, setDeletingFeedbackId] = useState('');
  // Pass 73 (§8) — a stable feedback id per draft, generated once, reused across retries/double-clicks; reset only
  // after a definite success. So a retry lands on the SAME child (idempotent), never a duplicate.
  const feedbackDraftIdRef = useRef('');
  const [feedbackComplete, setFeedbackComplete] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadMessage, setAudioUploadMessage] = useState('');
  const [audioPreviewURL, setAudioPreviewURL] = useState('');
  const [videoData, setVideoData] = useState(null);
  const [aiActionMessage, setAiActionMessage] = useState('');
  const [aiWorking, setAiWorking] = useState(false);
  const [aiDraftForm, setAiDraftForm] = useState(null);
  const [savingAiDraft, setSavingAiDraft] = useState(false);
  // Pass 74 — the PRIVATE AI working draft lives in a protected subcollection (never on the student-readable
  // video). The reviewer reads it live here; all mutations go through the authenticated backend.
  const [aiDraft, setAiDraft] = useState(null);
  const [reviewDrills, setReviewDrills] = useState([]);
  const [focusSkills, setFocusSkills] = useState([]);
  const [analysisForm, setAnalysisForm] = useState(null);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [drillActionMessage, setDrillActionMessage] = useState('');
  const [focusSkillMessage, setFocusSkillMessage] = useState('');
  const [builtInDrills, setBuiltInDrills] = useState([]);
  const [customDrillTemplates, setCustomDrillTemplates] = useState([]);
  const [loadingDrillTemplates, setLoadingDrillTemplates] = useState(false);
  const [savingCustomDrill, setSavingCustomDrill] = useState(false);
  const [customDrillMessage, setCustomDrillMessage] = useState('');
  const [drillFilters, setDrillFilters] = useState({
    level: 'all',
    search: '',
  });
  const [customDrillForm, setCustomDrillForm] = useState({
    title: '',
    description: '',
    discipline: '',
    level: 'all',
    topic: '',
    commonError: '',
    correctionCue: '',
    focusAreasText: '',
    tagsText: '',
    stepsText: '',
    visibility: 'private',
    relatedSkillIds: [],
  });
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const organizationId = getCanonicalOrgId(userData);

  // Pass 73 (§20/§44) — the ONE canonical feedback subscription (shared normalizer/sort with the student page).
  useEffect(() => {
    return subscribeTimestampedFeedback(db, videoId, setFeedbackList, (error) => {
      console.error('Failed to load timestamped feedback:', error);
      setFeedbackError('We could not load the feedback timeline right now.');
    });
  }, [videoId]);

  // Pass 74 — live-read the PRIVATE AI draft from the protected subcollection (reviewer-only per Rules).
  useEffect(() => {
    const draftRef = doc(db, 'videos', videoId, 'aiReviewDrafts', 'current');
    return onSnapshot(
      draftRef,
      (snap) => setAiDraft(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (error) => { console.error('Failed to load AI draft:', error); },
    );
  }, [videoId]);

  // Pass 73 (§74/§75) — load the parent video ONCE per video (was coupled to feedbackList.length, so every
  // add/delete refetched the whole video). Feedback changes no longer refetch the video.
  useEffect(() => {
    const loadVideoData = async () => {
      const videoDocRef = doc(db, 'videos', videoId);
      const videoSnap = await getDoc(videoDocRef);
      if (videoSnap.exists()) {
        const data = videoSnap.data();
        setVideoData(data);
        setReviewDrills(data.reviewDrills || []);
        setFocusSkills(data.focusSkills || []);
        setAnalysisForm(normalizeReviewAnalysis(data, data.analysis));
      }
    };

    loadVideoData().catch((error) => {
      console.error('Failed to load video data for AI review assistant:', error);
    });
  }, [videoId]);

  // §50 — revoke the local audio preview object URL on unmount (no leak across repeated mobile recordings).
  useEffect(() => () => { if (audioPreviewURL) { try { URL.revokeObjectURL(audioPreviewURL); } catch (_) {} } }, [audioPreviewURL]);

  const getAiFailureMessage = (error, draftErrorMessage) => {
    const rawMessage = draftErrorMessage || error?.message || '';
    const lowered = rawMessage.toLowerCase();

    if (lowered.includes('quota') || lowered.includes('billing')) {
      return 'AI draft generation is blocked because the OpenAI API key has no remaining quota or billing is not active.';
    }

    if (draftErrorMessage) {
      return draftErrorMessage;
    }

    if (rawMessage) {
      return rawMessage;
    }

    return 'Could not generate AI draft right now.';
  };

  const toMultiline = (items = []) => items.join('\n');

  const fromMultiline = (value) =>
    value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

  const fromCommaList = (value) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  useEffect(() => {
    if (aiDraft?.generationStatus === 'complete') {
      setAiDraftForm({
        summary: aiDraft.summary || '',
        focusAreasText: toMultiline(aiDraft.focusAreas || []),
        suggestedDrillsText: toMultiline(aiDraft.suggestedDrills || []),
        coachingNotesText: toMultiline(aiDraft.coachingNotes || []),
      });
    } else {
      setAiDraftForm(null);
    }
    // re-sync the editable form whenever the server draft content/approval changes (keyed on the change markers,
    // not the content fields themselves, so local typing isn't clobbered mid-edit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDraft?.generationStatus, aiDraft?.editedAt, aiDraft?.generatedAt, aiDraft?.approvalStatus]);

  useEffect(() => {
    setBuiltInDrills(getBuiltInDrillTemplatesBySport(videoData?.sport) || []);
  }, [videoData?.sport]);

  useEffect(() => {
    const loadCustomDrillTemplates = async () => {
      if (!currentUser?.uid) return;

      setLoadingDrillTemplates(true);
      try {
        const accessibleTemplates = await loadAccessibleDrillTemplates(db, {
          currentUser,
          userData,
          organizationMemberships: organizationId
            ? [{ organizationId, role: userData?.orgRole || 'instructor' }]
            : [],
        });

        setCustomDrillTemplates(
          normalizeDrillTemplates(accessibleTemplates, organizationId) || []
        );
      } catch (error) {
        console.error('Failed to load custom drill templates:', error);
      } finally {
        setLoadingDrillTemplates(false);
      }
    };

    loadCustomDrillTemplates();
  }, [currentUser, organizationId, userData]);

  // Pass 74 — edits go through the authenticated backend (which invalidates approval); the live subscription
  // reflects the saved draft. Editing an approved draft resets it to "needs approval".
  const saveAiDraftEdits = async (successMessage = 'AI draft edits saved. Approval reset — approve again to include them.') => {
    if (aiDraft?.generationStatus !== 'complete' || !aiDraftForm) return null;

    setSavingAiDraft(true);
    try {
      await editAiReviewDraft(videoId, {
        summary: aiDraftForm.summary.trim(),
        focusAreas: fromMultiline(aiDraftForm.focusAreasText),
        suggestedDrills: fromMultiline(aiDraftForm.suggestedDrillsText),
        coachingNotes: fromMultiline(aiDraftForm.coachingNotesText),
      });
      setAiActionMessage(successMessage);
      return true;
    } catch (error) {
      console.error('Failed to save AI draft edits:', error);
      setAiActionMessage(error?.message || 'Could not save AI draft edits right now.');
      return null;
    } finally {
      setSavingAiDraft(false);
    }
  };

  const persistReviewDrills = async (nextReviewDrills, successMessage) => {
    try {
      await updateDoc(doc(db, 'videos', videoId), {
        reviewDrills: nextReviewDrills,
      });
      setReviewDrills(nextReviewDrills);
      setDrillActionMessage(successMessage);
    } catch (error) {
      console.error('Failed to update review drills:', error);
      setDrillActionMessage('Could not update review drills right now.');
    }
  };

  const persistFocusSkills = async (nextFocusSkills, successMessage) => {
    try {
      await updateDoc(doc(db, 'videos', videoId), {
        focusSkills: nextFocusSkills,
      });
      setFocusSkills(nextFocusSkills);
      setFocusSkillMessage(successMessage);
    } catch (error) {
      console.error('Failed to update focus skills:', error);
      setFocusSkillMessage('Could not update focus skills right now.');
    }
  };

  const handleAddReviewDrill = async (template) => {
    const nextDrill = toReviewDrill(template, currentUser, organizationId);
    const exists = reviewDrills.some((item) => item.id === nextDrill.id);

    if (exists) {
      setDrillActionMessage('This drill is already attached to the review.');
      return;
    }

    await persistReviewDrills(
      [...reviewDrills, nextDrill],
      `"${nextDrill.title}" added to this review.`
    );
  };

  const handleRemoveReviewDrill = async (drillId) => {
    const nextReviewDrills = reviewDrills.filter((item) => item.id !== drillId);
    await persistReviewDrills(nextReviewDrills, 'Drill removed from this review.');
  };

  const handleAddFocusSkill = async (skill) => {
    if (!currentUser?.uid) return;

    const exists = focusSkills.some((item) => item.skillId === skill.skillId);
    if (exists) {
      setFocusSkillMessage('That focus skill is already attached to this review.');
      return;
    }

    if (focusSkills.length >= 3) {
      setFocusSkillMessage('You can attach up to 3 focus skills to one review.');
      return;
    }

    const nextFocusSkills = [
      ...focusSkills,
      {
        skillId: skill.skillId,
        treeId: skill.treeId,
        source: 'instructor_selected',
        selectedBy: currentUser.uid,
        selectedAt: new Date().toISOString(),
      },
    ];

    await persistFocusSkills(nextFocusSkills, `"${skill.name}" added as a focus skill.`);
  };

  const handleRemoveFocusSkill = async (skillId) => {
    const nextFocusSkills = focusSkills.filter((item) => item.skillId !== skillId);
    await persistFocusSkills(nextFocusSkills, 'Focus skill removed from this review.');
  };

  const handleCustomDrillFormChange = (field, value) => {
    setCustomDrillForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const setAnalysisSectionField = (section, field, value) => {
    setAnalysisForm((current) => ({
      ...(current || {}),
      [section]: {
        ...((current && current[section]) || {}),
        [field]: value,
      },
    }));
  };

  const setAnalysisStanceIssue = (field, value) => {
    setAnalysisForm((current) => ({
      ...(current || {}),
      cause: {
        ...((current && current.cause) || {}),
        stanceIssues: {
          ...((current && current.cause && current.cause.stanceIssues) || {}),
          [field]: value,
        },
      },
    }));
  };

  const toggleAnalysisArrayValue = (section, field, value) => {
    setAnalysisForm((current) => {
      const currentValues = current?.[section]?.[field] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...(current || {}),
        [section]: {
          ...((current && current[section]) || {}),
          [field]: nextValues,
        },
      };
    });
  };

  const saveStructuredAnalysis = async (successMessage) => {
    if (!videoData || (!isSnowboardVideo(videoData) && !isSkiVideo(videoData)) || !analysisForm) {
      return null;
    }

    const nextAnalysis = sanitizeReviewAnalysis(analysisForm);
    const defaultMessage = nextAnalysis?.variant === 'sas_snowboard_rider_analysis_v1'
      ? 'Snowboard rider analysis saved.'
      : 'Ski analysis saved.';
    setSavingAnalysis(true);
    try {
      await updateDoc(doc(db, 'videos', videoId), {
        analysis: nextAnalysis,
      });
      setVideoData((current) => ({
        ...(current || {}),
        analysis: nextAnalysis,
      }));
      if (successMessage !== null) {
        setAnalysisMessage(successMessage || defaultMessage);
      }
      return nextAnalysis;
    } catch (error) {
      console.error('Failed to save snowboard analysis:', error);
      setAnalysisMessage('Could not save the rider analysis right now.');
      return null;
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleCreateCustomDrill = async (event) => {
    event.preventDefault();

    if (!currentUser?.uid || !customDrillForm.title.trim() || !customDrillForm.description.trim()) {
      return;
    }

    setSavingCustomDrill(true);
    setCustomDrillMessage('');

    try {
      const payload = {
        title: customDrillForm.title.trim(),
        description: customDrillForm.description.trim(),
        discipline: customDrillForm.discipline.trim(),
        level: customDrillForm.level === 'all' ? [] : [customDrillForm.level],
        topic: customDrillForm.topic.trim(),
        commonError: customDrillForm.commonError.trim(),
        correctionCue: customDrillForm.correctionCue.trim(),
        focusAreas: fromCommaList(customDrillForm.focusAreasText),
        tags: fromCommaList(customDrillForm.tagsText),
        steps: fromMultiline(customDrillForm.stepsText),
        relatedSkillIds: normalizeSkillReferences(customDrillForm.relatedSkillIds),
        sport: videoData?.sport || '',
        visibility: organizationId && customDrillForm.visibility === 'org' ? 'org' : 'private',
        createdBy: currentUser.uid,
        orgId: organizationId || '',
        sourceType: 'instructor_custom',
        sourceLabel: organizationId && customDrillForm.visibility === 'org'
          ? 'School Library'
          : 'Instructor Library',
        ownerType:
          organizationId && customDrillForm.visibility === 'org'
            ? 'organization'
            : 'instructor',
      };

      const createdTemplate = await createDrillTemplate(db, payload, {
        currentUser,
        userData,
        organizationId,
      });
      const normalizedCreatedTemplate = normalizeDrillTemplate(createdTemplate, organizationId);
      setCustomDrillTemplates((current) =>
        [...(current || []), normalizedCreatedTemplate].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      );
      setCustomDrillForm({
        title: '',
        description: '',
        discipline: '',
        level: 'all',
        topic: '',
        commonError: '',
        correctionCue: '',
        focusAreasText: '',
        tagsText: '',
        stepsText: '',
        visibility: organizationId ? customDrillForm.visibility : 'private',
        relatedSkillIds: [],
      });
      setCustomDrillMessage('Custom drill saved to your library.');
    } catch (error) {
      console.error('Failed to create custom drill template:', error);
      setCustomDrillMessage('Could not save the custom drill right now.');
    } finally {
      setSavingCustomDrill(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const video = videoRef.current;
    if (!video || !content.trim() || submitting) return;

    const timestamp = Math.floor(video.currentTime);
    // one stable id per draft — reused on retry so an uncertain-result write is idempotent, not a duplicate.
    if (!feedbackDraftIdRef.current) feedbackDraftIdRef.current = doc(collection(db, 'videos', videoId, 'timestampedFeedback')).id;
    setSubmitting(true);
    setFeedbackError('');
    try {
      await createTimestampedFeedback(db, {
        videoId,
        feedbackId: feedbackDraftIdRef.current,
        timestamp,
        content: content.trim(),
        actorAuthUserId: currentUser?.uid || auth.currentUser?.uid || '',
      });
      // definite success → clear the draft and allocate a fresh id for the NEXT note (§47).
      setContent('');
      feedbackDraftIdRef.current = '';
    } catch (error) {
      // §47 — keep the draft text + stable id so the student can retry; surface the failure.
      console.error('Failed to save timestamped feedback:', error);
      setFeedbackError(error?.message || 'We could not save this feedback. Your note is still here — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJumpTo = (time) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = Math.max(0, time || 0); // §22 — never seek to a negative/NaN legacy timestamp
      video.play();
    }
  };

  const handleDelete = async (id) => {
    if (deletingFeedbackId) return;
    setDeletingFeedbackId(id);
    setFeedbackError('');
    try {
      await deleteTimestampedFeedback(db, { videoId, feedbackId: id });
    } catch (error) {
      // §48 — a delete failure is visible (the subscription keeps the item until the write succeeds).
      console.error('Failed to delete feedback:', error);
      setFeedbackError('We could not delete that note right now. Please try again.');
    } finally {
      setDeletingFeedbackId('');
    }
  };

  const MAX_AUDIO_BYTES = 25 * 1024 * 1024; // §32 — a review-level spoken note; 25 MB is ample, blocks garbage.

  const handleAudioFileChange = (e) => {
    const selectedFile = e.target.files[0];
    // §50 — revoke the previous object URL before replacing it (no leak on repeated selection).
    setAudioPreviewURL((prev) => { if (prev) { try { URL.revokeObjectURL(prev); } catch (_) {} } return ''; });
    setAudioUploadMessage('');
    if (!selectedFile) { setAudioFile(null); return; }
    // §32/§49 — validate before accepting: must be audio, non-empty, within the cap.
    if (!String(selectedFile.type || '').startsWith('audio/')) { setAudioFile(null); setAudioUploadMessage('Please choose an audio file.'); return; }
    if (selectedFile.size === 0) { setAudioFile(null); setAudioUploadMessage('That audio file is empty.'); return; }
    if (selectedFile.size > MAX_AUDIO_BYTES) { setAudioFile(null); setAudioUploadMessage('That audio note is too large (max 25 MB).'); return; }
    setAudioFile(selectedFile);
    setAudioPreviewURL(URL.createObjectURL(selectedFile));
  };

  const handleAudioUpload = async () => {
    if (!audioFile) return;

    try {
      setAudioUploading(true);
      setAudioUploadMessage('Uploading optional audio feedback...');

      const instructorId = auth.currentUser?.uid;
      const videoDocRef = doc(db, 'videos', videoId);
      const videoSnap = await getDoc(videoDocRef);

      if (!videoSnap.exists()) {
        throw new Error('Video not found.');
      }

      const videoData = videoSnap.data();
      const previousAudioPath = videoData.reviewAudioPath;
      const studentId = videoData.userId || 'student';
      // Pass 73A (§21/§22) — ONE canonical object per video at a FIXED name (no extension). The encoding lives in
      // the Storage contentType metadata (playback reads the Content-Type header), so the Storage match can be
      // exact and no stray alternate-extension object can ever accumulate.
      const storagePath = `feedbackAudio/${studentId}/${videoId}/review-audio`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, audioFile, { contentType: audioFile.type || 'audio/webm' });
      const reviewAudioURL = await getDownloadURL(storageRef);

      // §28 — only NOW that Storage holds the object do we point the video at it, with durable author provenance.
      await updateDoc(videoDocRef, {
        reviewAudioURL,
        reviewAudioPath: storagePath,
        reviewAudioUploadedAt: serverTimestamp(),
        reviewAudioUploadedByAuthUserId: instructorId || '',
      });

      if (previousAudioPath && previousAudioPath !== storagePath) {
        try {
          await deleteObject(ref(storage, previousAudioPath));
        } catch (deleteError) {
          console.warn('Could not delete previous audio object:', deleteError);
        }
      }

      setAudioUploadMessage('Audio feedback uploaded.');
    } catch (error) {
      console.error('❌ Failed to upload audio feedback:', error);
      setAudioUploadMessage('Audio upload failed. You can still submit text feedback.');
    } finally {
      setAudioUploading(false);
    }
  };

  const markAsComplete = async () => {
    try {
      // §52 — do NOT publish while ANY human review write is unresolved (was only audioUploading).
      if (audioUploading || submitting || deletingFeedbackId || savingAnalysis) {
        await Swal.fire({
          icon: 'info',
          title: 'A review write is still finishing',
          text: 'Please wait for your last feedback, audio, or analysis save to finish before completing the review.',
        });
        return;
      }

      const instructorId = auth.currentUser?.uid;

      const videoDocRef = doc(db, 'videos', videoId);
      const videoSnap = await getDoc(videoDocRef);
      const latestVideoData = videoSnap.data();
      if (!videoSnap.exists() || !latestVideoData) {
        throw new Error('Video not found.');
      }

      // Pass 73A (§14/§15) — the review is now FROZEN at Firestore once reviewed (an assigned instructor may no
      // longer mutate a reviewed video). So a completion RETRY must NOT re-attempt the video write:
      //   • already reviewed BY THIS caller → the publish succeeded; skip the (now-denied) video mutation and only
      //     recover the deterministic, idempotent side effects (thread + feedback_ready notification).
      //   • already reviewed BY ANOTHER actor → fail visibly; never overwrite their reviewedBy.
      if (latestVideoData.status === 'reviewed') {
        if ((latestVideoData.reviewedBy || '') !== (instructorId || '')) {
          await Swal.fire({
            icon: 'info',
            title: 'Already completed',
            text: 'This review was already completed by someone else. Refresh to see the published version.',
          });
          return;
        }
        // Pass 75 — IDEMPOTENT RECOVERY only (no video write). deliverReviewedVideo re-verifies the persisted
        // reviewed state + reviewer, then ensures ONLY the MISSING deterministic side effects (canonical thread +
        // intro + create-once feedback_ready). It never reorders an active conversation nor resets read state.
        try {
          await deliverReviewedVideo(db, { videoId, reviewerAuthUid: instructorId });
        } catch (err) {
          console.warn('Review delivery recovery failed:', err);
        }
        const retryPublish = await publishApprovedAiIfAny(); // idempotent recovery of an approved-AI publication
        if (retryPublish === 'failed') { await warnAiPublishFailed(); return; } // stay so the coach can retry
        setFeedbackComplete(true);
        setTimeout(() => { navigate('/dashboard'); }, 2000);
        return;
      }
      // §53 — a completely empty review cannot publish. Require at least ONE human coaching artifact: a
      // timestamped comment, review-level audio, a human drill/focus selection, or a meaningful analysis. An
      // unapproved AI draft does NOT count.
      const hasHumanEvidence = feedbackList.length > 0 ||
        Boolean(latestVideoData.reviewAudioURL) ||
        (Array.isArray(reviewDrills) && reviewDrills.length > 0) ||
        (Array.isArray(focusSkills) && focusSkills.length > 0) ||
        Boolean(analysisForm && hasMeaningfulAnalysis(analysisForm));
      if (!hasHumanEvidence) {
        await Swal.fire({
          icon: 'info',
          title: 'Add some feedback first',
          text: 'Add at least one timestamped comment, an audio note, a drill/focus, or rider analysis before completing the review.',
        });
        return;
      }
      if ((isSnowboardVideo(latestVideoData) || isSkiVideo(latestVideoData)) && analysisForm && hasMeaningfulAnalysis(analysisForm)) {
        await saveStructuredAnalysis(null);
      }

      // Pass 74A (§8/§9) — revalidate any approved AI draft WHILE still pending; a stale draft prompts the coach
      // to regenerate or complete without AI. 'abort' stops completion so nothing is silently lost.
      const preflight = await preflightApprovedAi();
      if (preflight === 'abort') return;

      // Pass 75 (§3) — DELIVERY SIDE EFFECTS COME AFTER CANONICAL REVIEW STATE. Persist status=reviewed FIRST;
      // this is the authoritative review truth. If THIS write fails/denies, control never reaches delivery, so
      // NO review-ready thread intro and NO feedback_ready notification are created (closes the ordering defect).
      await updateDoc(videoDocRef, {
        status: 'reviewed',
        reviewedAt: serverTimestamp(),
        reviewedBy: instructorId,
      });

      // The review is now authoritative — deliver the deterministic, idempotent side effects (canonical thread +
      // intro message + create-once feedback_ready notification). A delivery failure NEVER rolls the review back
      // to pending; a retry recovers only the missing artifacts.
      try {
        await deliverReviewedVideo(db, { videoId, reviewerAuthUid: instructorId });
      } catch (err) {
        console.warn('Review delivery failed (review remains reviewed; retry can recover):', err);
      }

      // Pass 74/74A — the review is now published; copy an APPROVED private AI draft to the student (only after
      // 'reviewed', so the student never raw-reads it earlier). Unapproved/absent → nothing published. If a fresh
      // approved draft FAILS to publish, stay and tell the coach truthfully (retryable) rather than imply success.
      const publishResult = await publishApprovedAiIfAny();
      if (publishResult === 'failed') { await warnAiPublishFailed(); return; }

      setFeedbackComplete(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('❌ Failed to mark feedback complete:', err);
      Swal.fire({
        icon: 'error',
        title: 'Could not complete review',
        text: 'Something went wrong. Please try again.',
      });
    }
  };

  const handleGenerateAiDraft = async ({ regenerate = false } = {}) => {
    setAiWorking(true);
    setAiActionMessage(regenerate ? 'Regenerating AI draft…' : 'Generating AI draft…');

    try {
      if (videoData && (isSnowboardVideo(videoData) || isSkiVideo(videoData)) && analysisForm && hasMeaningfulAnalysis(analysisForm)) {
        const savedAnalysis = await saveStructuredAnalysis(null);
        if (!savedAnalysis) {
          throw new Error('Could not save structured analysis before generating AI draft.');
        }
      }
      // The private draft updates live via the subscription; no parent refetch needed.
      await generateAiReviewDraft(videoId, { regenerate });
      setAiActionMessage('AI draft ready — review and edit it privately, then Approve to include it.');
    } catch (error) {
      console.error('Failed to generate AI draft:', error);
      setAiActionMessage(getAiFailureMessage(error, error?.message));
    } finally {
      setAiWorking(false);
    }
  };

  // Pass 74 — explicit human approval. Saves any pending edits first (which would otherwise reset approval),
  // then approves the current content. The server refuses a stale draft (evidence changed → regenerate).
  const handleApproveAiDraft = async () => {
    setAiWorking(true);
    setAiActionMessage('Approving AI notes…');
    try {
      if (aiDraftForm) {
        const saved = await saveAiDraftEdits(null);
        if (!saved) throw new Error('Could not save your edits before approving.');
      }
      await approveAiReviewDraft(videoId);
      setAiActionMessage('AI notes approved. They will be published to the student when you complete the review.');
    } catch (error) {
      console.error('Failed to approve AI draft:', error);
      setAiActionMessage(error?.message || 'Could not approve the AI draft right now.');
    } finally {
      setAiWorking(false);
    }
  };

  // Pass 74A (§8/§9) — BEFORE writing status=reviewed (which freezes generate/edit/approve), revalidate any
  // approved AI draft while the parent is still pending. Returns 'none' | 'ok' | 'abort':
  //   • no approved draft → 'ok' (nothing to do)
  //   • approved + still fresh → idempotent re-approve succeeds → 'ok'
  //   • approved + STALE → ask the coach: Regenerate ('abort' completion) or Complete Without AI (unapprove → 'ok')
  const preflightApprovedAi = async () => {
    let snap;
    try {
      snap = await getDoc(doc(db, 'videos', videoId, 'aiReviewDrafts', 'current'));
    } catch (_) { return 'ok'; }
    if (!snap.exists() || snap.data()?.approvalStatus !== 'approved') return 'ok';
    try {
      await approveAiReviewDraft(videoId); // idempotent re-approve = freshness + actor + hash revalidation
      return 'ok';
    } catch (error) {
      if (error?.status !== 409) throw error;
      const choice = await Swal.fire({
        icon: 'warning',
        title: 'The review changed after you approved the AI notes',
        text: 'Regenerate the AI notes to include them, or complete the review without them.',
        showCancelButton: true,
        confirmButtonText: 'Regenerate AI Notes',
        cancelButtonText: 'Complete Without AI',
      });
      if (choice.isConfirmed) {
        setAiActionMessage('Regenerate and re-approve the AI draft, then complete the review again.');
        return 'abort';
      }
      try { await unapproveAiReview(videoId); } catch (_) { /* best-effort; publish still gated server-side */ }
      return 'ok';
    }
  };

  // Pass 74/74A (§11/§13/§36) — after status=reviewed, copy an APPROVED FRESH draft to the student-visible
  // aiReview via the authenticated backend. Decides from an AUTHORITATIVE fresh read (not the lagging
  // subscription); the backend re-verifies regardless. Returns 'none' | 'ok' | 'failed' so the caller can be
  // TRUTHFUL: a failure must NOT roll back the human review, but must not pretend the AI was published either.
  const publishApprovedAiIfAny = async () => {
    let snap;
    try {
      snap = await getDoc(doc(db, 'videos', videoId, 'aiReviewDrafts', 'current'));
    } catch (_) { return 'none'; }
    if (!snap.exists() || snap.data()?.approvalStatus !== 'approved') return 'none';
    try {
      await publishAiReview(videoId);
      return 'ok';
    } catch (error) {
      console.warn('Approved AI notes could not be published yet (review is still complete):', error);
      return 'failed';
    }
  };

  const warnAiPublishFailed = async () => {
    await Swal.fire({
      icon: 'info',
      title: 'Review completed',
      text: 'Your review is complete, but the approved AI notes were not published yet. You can retry from this review.',
    });
  };

  // aiDraft is the live PRIVATE draft from the subcollection (state above). aiPublished is the student-visible
  // approved output on the parent video. They have DISTINCT semantics — never conflate draft with published.
  const aiPublished = videoData?.aiReview;
  const aiDraftReady = aiDraft?.generationStatus === 'complete';
  const aiApproved = aiDraft?.approvalStatus === 'approved';
  const normalizedReviewDrills = normalizeDrillTemplates(reviewDrills, organizationId) || [];
  const availableTemplates = [
    ...(builtInDrills || []),
    ...(customDrillTemplates || []),
  ].filter((template) =>
    matchesDrillFilter(template, {
      sport: videoData?.sport,
      level: drillFilters.level,
      search: drillFilters.search,
    })
  );
  const groupedDrills = groupDrillTemplates(
    availableTemplates,
    currentUser?.uid,
    organizationId
  ) || { sas: [], mine: [], school: [] };
  const currentSport = videoData?.sport || 'Sport not set';
  const isSnowboardReview = isSnowboardVideo(videoData);
  const isSkiReview = isSkiVideo(videoData);
  const analysisDisplay = analysisForm ? getAnalysisDisplayMeta(analysisForm.variant) : null;
  const analysisEditorFields = analysisDisplay?.editorFields || {};
  const hasStructuredAnalysisStep = isSnowboardReview || isSkiReview;
  const availableSkills = getAvailableSkillsForVideo(videoData) || [];
  const selectedFocusSkills = resolveFocusSkillReferences(videoData, focusSkills) || [];
  const groupedAvailableSkills = availableSkills.reduce(
    (groups, skill) => {
      groups[skill.groupType] = groups[skill.groupType] || [];
      groups[skill.groupType].push(skill);
      return groups;
    },
    {}
  );
  const customDrillRelatedSkills = resolveFocusSkillReferences(
    videoData,
    customDrillForm.relatedSkillIds || []
  ) || [];

  const handleToggleCustomDrillSkill = (skill) => {
    setCustomDrillForm((current) => {
      const currentSkills = current.relatedSkillIds || [];
      const exists = currentSkills.some((item) => item.skillId === skill.skillId);

      if (exists) {
        return {
          ...current,
          relatedSkillIds: currentSkills.filter((item) => item.skillId !== skill.skillId),
        };
      }

      if (currentSkills.length >= 2) {
        setCustomDrillMessage('Link up to 2 skills per custom drill in this first pass.');
        return current;
      }

      setCustomDrillMessage('');
      return {
        ...current,
        relatedSkillIds: [
          ...currentSkills,
          {
            skillId: skill.skillId,
            treeId: skill.treeId,
          },
        ],
      };
    });
  };

  const renderDrillCard = (template) => (
    <div key={template.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">{template.title}</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">{template.description}</p>
        </div>
        <button
          type="button"
          onClick={() => handleAddReviewDrill(template)}
          className="shrink-0 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
        >
          Add to Review
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
        {template.sourceLabel && (
          <span className="rounded-full bg-slate-100 px-2 py-1">{template.sourceLabel}</span>
        )}
        {template.topic && (
          <span className="rounded-full bg-slate-100 px-2 py-1">{template.topic}</span>
        )}
        {template.level?.length > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-1">{template.level.join(', ')}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="mt-4 rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm md:p-6">
      <div className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-2xl font-semibold text-slate-950">Feedback Editor</h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Review the run, add timestamped notes, shape any AI support, and attach drills before you complete the review.
          </p>
        </div>
        <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-3">
          <div className="rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400">Sport</p>
            <p className="mt-1 font-medium text-slate-900">{currentSport}</p>
          </div>
          <div className="rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400">Feedback Notes</p>
            <p className="mt-1 font-medium text-slate-900">{feedbackList.length}</p>
          </div>
          <div className="rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-400">Organization</p>
            <p className="mt-1 font-medium text-slate-900">
              {userData?.orgName || organizationId || 'No org assigned'}
              {userData?.orgRole ? ` (${userData.orgRole})` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.9fr)]">
        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step 1</p>
              <h4 className="mt-2 text-lg font-semibold text-slate-950">Video Review</h4>
              <p className="mt-1 text-sm text-slate-600">
                Play, pause, and scrub the video, then add timestamped coaching notes at the right moment.
              </p>
            </div>
            <video
              ref={videoRef}
              src={videoURL}
              controls
              className="w-full rounded-2xl bg-slate-950"
            />
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step 2</p>
                <h4 className="mt-2 text-lg font-semibold text-slate-950">Timestamped Feedback</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Keep notes short and specific. Students can click them later to jump to the exact moment.
                </p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {feedbackList.length} note{feedbackList.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="space-y-3">
              {feedbackList.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No timestamped feedback yet. Pause the video and add your first coaching note below.
                </div>
              ) : (
                feedbackList.map((fb) => (
                  <div key={fb.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="mb-2 inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                          {fb.timestamp}s
                        </div>
                        <p className="text-sm leading-6 text-slate-800">{fb.content}</p>
                      </div>
                      <div className="flex shrink-0 gap-2 sm:flex-col">
                        <button
                          onClick={() => handleJumpTo(fb.timestamp)}
                          className="rounded-full border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-700"
                        >
                          Jump to {fb.timestamp}s
                        </button>
                        <button
                          onClick={() => handleDelete(fb.id)}
                          className="rounded-full border border-red-200 bg-white px-3 py-1 text-sm font-medium text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleSubmit} className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {feedbackError ? (
                <div role="alert" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{feedbackError}</div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Feedback Type</label>
                  <select
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                  >
                    <option value="text">Text</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Add Feedback</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                    rows={4}
                    placeholder="Write a focused coaching note for the current video moment..."
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-4 w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </section>
        </div>

        <div className="space-y-6 xl:sticky xl:top-4 xl:self-start">
          {isSnowboardReview && analysisForm && (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step 3</p>
                <h4 className="mt-2 text-lg font-semibold text-slate-950">{analysisDisplay?.editorTitle || 'Snowboard Rider Analysis'}</h4>
                <p className="mt-1 text-sm text-slate-600">
                  {analysisDisplay?.editorDescription || 'Use the Detect → Cause → Effect → Correct → Train workflow to turn the ride into a structured movement analysis. This variant is snowboard-specific.'}
                </p>
              </div>

              {analysisMessage && (
                <p className="mb-3 text-sm text-slate-600">{analysisMessage}</p>
              )}

              <div className="space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Detect</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {(analysisEditorFields.detectSelects || []).map((field) => (
                      <div key={field.key}>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{field.label}</label>
                        <select
                          value={analysisForm.detect[field.key] || ''}
                          onChange={(event) => setAnalysisSectionField('detect', field.key, event.target.value)}
                          className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                        >
                          <option value="">{field.placeholder}</option>
                          {(field.options || []).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  {(analysisEditorFields.detectTextareas || []).slice(0, 1).map((field) => (
                    <textarea
                      key={field.key}
                      value={analysisForm.detect[field.key] || ''}
                      onChange={(event) => setAnalysisSectionField('detect', field.key, event.target.value)}
                      rows={field.rows || 3}
                      placeholder={field.placeholder}
                      className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                    />
                  ))}
                  <div className="mt-3">
                    <p className="mb-2 text-sm font-medium text-slate-700">Observed Inefficiencies</p>
                    <div className="flex flex-wrap gap-2">
                      {SNOWBOARD_INEFFICIENCY_TAGS.map((tag) => {
                        const selected = analysisForm.detect.observedInefficiencies.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleAnalysisArrayValue('detect', 'observedInefficiencies', tag)}
                            className={`rounded-full border px-3 py-1.5 text-sm ${
                              selected
                                ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                : 'border-slate-300 bg-white text-slate-700'
                            }`}
                          >
                            {getSnowboardAnalysisLabel('inefficiency', tag)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {(analysisEditorFields.detectTextareas || []).slice(1).map((field) => (
                    <textarea
                      key={field.key}
                      value={analysisForm.detect[field.key] || ''}
                      onChange={(event) => setAnalysisSectionField('detect', field.key, event.target.value)}
                      rows={field.rows || 3}
                      placeholder={field.placeholder}
                      className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                    />
                  ))}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Cause</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {(analysisDisplay?.stanceIssueFields || ['ankles', 'knees', 'hips', 'upperBody', 'foreAft', 'lateral', 'vertical', 'rotational']).map((field) => (
                      <input
                        key={field}
                        type="text"
                        value={analysisForm.cause.stanceIssues[field] || ''}
                        onChange={(event) => setAnalysisStanceIssue(field, event.target.value)}
                        placeholder={field === 'upperBody' ? 'upper body issue' : `${field} issue`}
                        className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                      />
                    ))}
                  </div>
                  <textarea
                    value={analysisForm.cause[analysisEditorFields.causeMovement?.key || 'movementDescription']}
                    onChange={(event) => setAnalysisSectionField('cause', analysisEditorFields.causeMovement?.key || 'movementDescription', event.target.value)}
                    rows={analysisEditorFields.causeMovement?.rows || 3}
                    placeholder={analysisEditorFields.causeMovement?.placeholder || 'Describe the inefficient stance and movement objectively'}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Effect</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SNOWBOARD_BOARD_PERFORMANCE_ISSUES.map((issue) => {
                      const selected = analysisForm.effect.boardPerformanceIssues.includes(issue);
                      return (
                        <button
                          key={issue}
                          type="button"
                          onClick={() => toggleAnalysisArrayValue('effect', 'boardPerformanceIssues', issue)}
                          className={`rounded-full border px-3 py-1.5 text-sm ${
                            selected
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-300 bg-white text-slate-700'
                          }`}
                        >
                          {getSnowboardAnalysisLabel('boardPerformance', issue)}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={analysisForm.effect.effectDescription}
                    onChange={(event) => setAnalysisSectionField('effect', 'effectDescription', event.target.value)}
                    rows={3}
                    placeholder="Describe what the board is doing as an effect of the movement pattern"
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Correct</p>
                  <div className="space-y-3 mt-3">
                    {(analysisEditorFields.correctTextareas || []).map((field) => (
                      <textarea
                        key={field.key}
                        value={analysisForm.correct[field.key] || ''}
                        onChange={(event) => setAnalysisSectionField('correct', field.key, event.target.value)}
                        rows={field.rows || 3}
                        placeholder={field.label}
                        className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Train</p>
                  <p className="mt-1 text-sm text-slate-600">
                    Use review drills below as the formal training plan. Add any extra rider-analysis training notes here.
                  </p>
                  {(analysisEditorFields.trainTextareas || []).map((field) => (
                    <textarea
                      key={field.key}
                      value={analysisForm.train[field.key] || ''}
                      onChange={(event) => setAnalysisSectionField('train', field.key, event.target.value)}
                      rows={field.rows || 3}
                      placeholder={field.placeholder || field.label}
                      className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => saveStructuredAnalysis()}
                  disabled={savingAnalysis}
                  className="w-full rounded-2xl border border-slate-800 bg-slate-800 px-4 py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingAnalysis ? (analysisDisplay?.savingLabel || 'Saving rider analysis...') : (analysisDisplay?.saveLabel || 'Save Rider Analysis')}
                </button>
              </div>
            </section>
          )}

          {isSkiReview && analysisForm && (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step 3</p>
                <h4 className="mt-2 text-lg font-semibold text-slate-950">{analysisDisplay?.editorTitle || 'Ski Analysis'}</h4>
                <p className="mt-1 text-sm text-slate-600">
                  {analysisDisplay?.editorDescription || 'Use the shared Detect → Cause → Effect → Correct → Train workflow in a ski-specific way: overall impression, ski performance, body movements, ideal comparison, and lesson plan.'}
                </p>
              </div>

              {analysisMessage && (
                <p className="mb-3 text-sm text-slate-600">{analysisMessage}</p>
              )}

              <div className="space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Detect</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {(analysisEditorFields.detectInputs || []).map((field) => (
                      <input
                        key={field.key}
                        type="text"
                        value={analysisForm.detect[field.key] || ''}
                        onChange={(event) => setAnalysisSectionField('detect', field.key, event.target.value)}
                        placeholder={field.placeholder}
                        className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                      />
                    ))}
                  </div>
                  {(analysisEditorFields.detectSelects || []).map((field) => (
                    <div key={field.key} className="mt-3">
                      <label className="mb-1 block text-sm font-medium text-slate-700">{field.label}</label>
                      <select
                        value={analysisForm.detect[field.key] || ''}
                        onChange={(event) => setAnalysisSectionField('detect', field.key, event.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                      >
                        <option value="">{field.placeholder}</option>
                        {(field.options || []).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <div className="mt-3">
                    <p className="mb-2 text-sm font-medium text-slate-700">Primary Performance Issues</p>
                    <div className="flex flex-wrap gap-2">
                      {SKI_PRIMARY_PERFORMANCE_ISSUES.map((issue) => {
                        const selected = analysisForm.detect.primaryPerformanceIssues.includes(issue);
                        return (
                          <button
                            key={issue}
                            type="button"
                            onClick={() => toggleAnalysisArrayValue('detect', 'primaryPerformanceIssues', issue)}
                            className={`rounded-full border px-3 py-1.5 text-sm ${
                              selected
                                ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                : 'border-slate-300 bg-white text-slate-700'
                            }`}
                          >
                            {getSkiAnalysisLabel('primary', issue)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Cause</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {(analysisDisplay?.stanceIssueFields || ['ankles', 'knees', 'hips', 'upperBody', 'foreAft', 'lateralBalance', 'rotaryInput', 'pressureManagement']).map((field) => (
                      <input
                        key={field}
                        type="text"
                        value={analysisForm.cause.stanceIssues[field] || ''}
                        onChange={(event) => setAnalysisStanceIssue(field, event.target.value)}
                        placeholder={field}
                        className="w-full rounded-xl border border-slate-300 bg-white p-2.5 text-sm"
                      />
                    ))}
                  </div>
                  <textarea
                    value={analysisForm.cause[analysisEditorFields.causeMovement?.key || 'movementDescription']}
                    onChange={(event) => setAnalysisSectionField('cause', analysisEditorFields.causeMovement?.key || 'movementDescription', event.target.value)}
                    rows={analysisEditorFields.causeMovement?.rows || 3}
                    placeholder={analysisEditorFields.causeMovement?.placeholder || 'Describe the key body movements causing the ski performance'}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Effect</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SKI_PERFORMANCE_ISSUES.map((issue) => {
                      const selected = analysisForm.effect.skiPerformanceIssues.includes(issue);
                      return (
                        <button
                          key={issue}
                          type="button"
                          onClick={() => toggleAnalysisArrayValue('effect', 'skiPerformanceIssues', issue)}
                          className={`rounded-full border px-3 py-1.5 text-sm ${
                            selected
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-300 bg-white text-slate-700'
                          }`}
                        >
                          {getSkiAnalysisLabel('performance', issue)}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={analysisForm.effect.effectDescription}
                    onChange={(event) => setAnalysisSectionField('effect', 'effectDescription', event.target.value)}
                    rows={3}
                    placeholder="Describe what the skis are doing as an outcome"
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Correct</p>
                  <div className="space-y-3 mt-3">
                    {(analysisEditorFields.correctTextareas || []).map((field) => (
                      <textarea
                        key={field.key}
                        value={analysisForm.correct[field.key] || ''}
                        onChange={(event) => setAnalysisSectionField('correct', field.key, event.target.value)}
                        rows={field.rows || 3}
                        placeholder={field.label}
                        className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Train</p>
                  <div className="space-y-3 mt-3">
                    {(analysisEditorFields.trainTextareas || []).map((field) => (
                      <textarea
                        key={field.key}
                        value={analysisForm.train[field.key] || ''}
                        onChange={(event) => setAnalysisSectionField('train', field.key, event.target.value)}
                        rows={field.rows || 3}
                        placeholder={field.label}
                        className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm"
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => saveStructuredAnalysis()}
                  disabled={savingAnalysis}
                  className="w-full rounded-2xl border border-slate-800 bg-slate-800 px-4 py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingAnalysis ? (analysisDisplay?.savingLabel || 'Saving ski analysis...') : (analysisDisplay?.saveLabel || 'Save Ski Analysis')}
                </button>
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step {hasStructuredAnalysisStep ? '4' : '3'}</p>
              <h4 className="mt-2 text-lg font-semibold text-slate-950">Optional Review Audio</h4>
              <p className="mt-1 text-sm text-slate-600">
                Add one review-level audio note if a spoken walkthrough will help. Text feedback remains the primary record.
              </p>
            </div>
            <input
              type="file"
              accept="audio/*"
              onChange={handleAudioFileChange}
              className="w-full text-sm"
            />
            {audioPreviewURL && (
              <audio controls src={audioPreviewURL} className="mt-3 w-full">
                Your browser does not support audio playback.
              </audio>
            )}
            <button
              type="button"
              onClick={handleAudioUpload}
              disabled={!audioFile || audioUploading}
              className="mt-4 w-full rounded-2xl bg-slate-800 px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              {audioUploading ? 'Uploading audio...' : 'Upload Audio Note'}
            </button>
            {audioUploadMessage && (
              <p className="mt-3 text-sm text-slate-600">{audioUploadMessage}</p>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step {hasStructuredAnalysisStep ? '5' : '4'}</p>
              <h4 className="mt-2 text-lg font-semibold text-slate-950">AI Review Assistant <span className="ml-2 align-middle rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Private</span></h4>
              <p className="mt-1 text-sm text-slate-600">
                Generate an optional AI draft, edit it, and <strong>approve</strong> it. The student sees nothing unless you
                approve it and complete the review. AI is optional — you can complete a review with human feedback alone.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleGenerateAiDraft({ regenerate: aiDraftReady })}
                disabled={aiWorking || (feedbackList.length === 0 && reviewDrills.length === 0 && focusSkills.length === 0)}
                className="inline-flex items-center justify-center rounded-full border border-indigo-700 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {aiWorking ? 'Working…' : aiDraftReady ? 'Regenerate AI Draft' : 'Generate AI Draft'}
              </button>

              <button
                type="button"
                onClick={handleApproveAiDraft}
                disabled={aiWorking || !aiDraftReady || aiApproved}
                className="inline-flex items-center justify-center rounded-full border border-green-700 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {aiApproved ? '✓ Approved' : 'Approve AI Notes'}
              </button>
            </div>

            {aiActionMessage && (
              <p className="mt-3 text-sm text-slate-600">{aiActionMessage}</p>
            )}

            {aiApproved ? (
              <p className="mt-3 rounded-2xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Approved — these AI notes will be published to the student when you complete the review. Editing or regenerating resets approval.
              </p>
            ) : aiDraftReady ? (
              <p className="mt-3 rounded-2xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                This AI draft is <strong>private</strong> and will NOT be shown to the student unless you approve it. If you changed your review after generating, regenerate before approving.
              </p>
            ) : null}

            {!aiDraft ? (
              <p className="mt-4 text-sm text-slate-500">
                No AI draft yet. Generating one is optional.
              </p>
            ) : aiDraft.generationStatus === 'pending' ? (
              <p className="mt-4 text-sm text-slate-500">
                AI draft generation is in progress…
              </p>
            ) : aiDraft.generationStatus === 'failed' ? (
              <div className="mt-4 space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <p>AI draft generation failed. You can try again — the human review is unaffected.</p>
                {aiDraft.errorMessage && <p className="font-medium">{aiDraft.errorMessage}</p>}
              </div>
            ) : (
              <div className="mt-4 space-y-4 rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
                <div>
                  <p className="text-sm font-semibold text-indigo-900">AI Draft Summary</p>
                  <textarea
                    value={aiDraftForm?.summary || ''}
                    onChange={(event) =>
                      setAiDraftForm((current) => ({
                        ...(current || {}),
                        summary: event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-xl border border-indigo-200 bg-white p-3 text-sm text-indigo-950"
                    rows={3}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">Focus Areas</p>
                    <textarea
                      value={aiDraftForm?.focusAreasText || ''}
                      onChange={(event) =>
                        setAiDraftForm((current) => ({
                          ...(current || {}),
                          focusAreasText: event.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-xl border border-indigo-200 bg-white p-3 text-sm text-indigo-950"
                      rows={5}
                      placeholder="One focus area per line"
                    />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-indigo-900">Suggested Drills</p>
                    <textarea
                      value={aiDraftForm?.suggestedDrillsText || ''}
                      onChange={(event) =>
                        setAiDraftForm((current) => ({
                          ...(current || {}),
                          suggestedDrillsText: event.target.value,
                        }))
                      }
                      className="mt-2 w-full rounded-xl border border-indigo-200 bg-white p-3 text-sm text-indigo-950"
                      rows={5}
                      placeholder="One drill per line"
                    />
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-indigo-900">Coaching Notes</p>
                  <textarea
                    value={aiDraftForm?.coachingNotesText || ''}
                    onChange={(event) =>
                      setAiDraftForm((current) => ({
                        ...(current || {}),
                        coachingNotesText: event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-xl border border-indigo-200 bg-white p-3 text-sm text-indigo-950"
                    rows={4}
                    placeholder="One coaching note per line"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => saveAiDraftEdits()}
                  disabled={savingAiDraft}
                  className="inline-flex items-center justify-center rounded-full border border-indigo-700 bg-white px-4 py-2 text-sm font-medium text-indigo-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingAiDraft ? 'Saving draft...' : 'Save AI Draft Edits'}
                </button>
              </div>
            )}

            {aiPublished?.status === 'complete' && (
              <p className="mt-4 text-sm text-emerald-700">
                Published AI notes are currently visible on the student review page.
              </p>
            )}
          </section>

          {videoData?.userId ? (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">AI Copilot</p>
                <h4 className="mt-2 text-lg font-semibold text-slate-950">Review Summary Copilot</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Add quick coaching notes and let AI draft a structured review summary. Review and edit before applying — AI never writes to your review directly.
                </p>
              </div>
              <AiCopilotPanel
                draftType="review_summary"
                signals={buildClientSignalsFromWorkspace(
                  {
                    clientProfile: { userId: videoData.userId },
                    videos: [],
                    lessons: [],
                    bookings: [],
                    feedbackHistory: [],
                    packageSummary: { activeMemberships: [], servicePackages: [] },
                    clientNotes: [],
                  },
                  organizationId
                )}
                applyFn={({ draftText, editableFields }) => {
                  setAiDraftForm((current) => ({
                    ...(current || {}),
                    summary: draftText || current?.summary || '',
                    coachingNotesText:
                      [editableFields?.studentNote, editableFields?.nextStepRecommendation]
                        .filter(Boolean)
                        .join('\n') || current?.coachingNotesText || '',
                  }));
                }}
                onDiscard={() => {}}
              />
            </section>
          ) : null}

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step {hasStructuredAnalysisStep ? '6' : '5'}</p>
              <h4 className="mt-2 text-lg font-semibold text-slate-950">Review Drills</h4>
              <p className="mt-1 text-sm text-slate-600">
                Attach structured drills to this review. These show up on the student review page and help keep coaching consistent.
              </p>
            </div>

            {drillActionMessage && (
              <p className="mb-3 text-sm text-slate-600">{drillActionMessage}</p>
            )}

            <div className="space-y-3">
              {normalizedReviewDrills.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No drills are attached to this review yet.
                </div>
              ) : (
                normalizedReviewDrills.map((drill) => (
                  <div key={drill.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{drill.title}</p>
                        <p className="mt-1 text-sm text-slate-700">{drill.description}</p>
                        <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">
                          {drill.sourceLabel || 'Instructor Library'} {drill.topic ? `• ${drill.topic}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveReviewDrill(drill.id)}
                        className="rounded-full border border-red-200 bg-white px-3 py-1 text-sm text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {availableSkills.length > 0 && (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step {hasStructuredAnalysisStep ? '7' : '6'}</p>
                <h4 className="mt-2 text-lg font-semibold text-slate-950">Focus Skills</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Optionally attach up to 3 SAS ski skills this review is focused on. These become the strongest signal in student progress.
                </p>
              </div>

              {focusSkillMessage && (
                <p className="mb-3 text-sm text-slate-600">{focusSkillMessage}</p>
              )}

              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Selected for this review</p>
                  {selectedFocusSkills.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-500">
                      No instructor-selected focus skills yet.
                    </p>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedFocusSkills.map((skill) => (
                        <button
                          key={skill.skillId}
                          type="button"
                          onClick={() => handleRemoveFocusSkill(skill.skillId)}
                          className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
                        >
                          {skill.name} ×
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {Object.entries(groupedAvailableSkills).map(([groupName, skills]) => (
                    <div key={groupName}>
                      <p className="mb-2 text-sm font-semibold text-slate-900 capitalize">{groupName}</p>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((skill) => {
                          const selected = selectedFocusSkills.some((item) => item.skillId === skill.skillId);
                          return (
                            <button
                              key={skill.skillId}
                              type="button"
                              onClick={() => handleAddFocusSkill(skill)}
                              disabled={selected || selectedFocusSkills.length >= 3}
                              className="rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {skill.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Step {availableSkills.length > 0 ? (hasStructuredAnalysisStep ? '8' : '7') : (hasStructuredAnalysisStep ? '7' : '6')}</p>
              <h4 className="mt-2 text-lg font-semibold text-slate-950">Reusable Drill Library</h4>
              <p className="mt-1 text-sm text-slate-600">
                Use SAS draft templates, your own private drills, or school-shared drills when your instructor account belongs to an organization.
              </p>
              <p className="mt-2 text-sm text-slate-700">
                Current org context:{' '}
                <span className="font-medium">{userData?.orgName || organizationId || 'No org assigned'}</span>
                {userData?.orgRole ? ` (${userData.orgRole})` : ''}
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Sport</label>
                <input
                  type="text"
                  value={currentSport}
                  readOnly
                  className="w-full rounded-xl border border-slate-300 bg-slate-100 p-2.5 text-sm text-slate-600"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Level Filter</label>
                <select
                  value={drillFilters.level}
                  onChange={(event) =>
                    setDrillFilters((current) => ({
                      ...current,
                      level: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                >
                  <option value="all">All levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Topic / Tag Search</label>
                <input
                  type="text"
                  value={drillFilters.search}
                  onChange={(event) =>
                    setDrillFilters((current) => ({
                      ...current,
                      search: event.target.value,
                    }))
                  }
                  placeholder="Search topic, error, cue, tags..."
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
              </div>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">SAS Drills</p>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">{groupedDrills.sas.length}</span>
                </div>
                {groupedDrills.sas.length === 0 ? (
                  <p className="text-sm text-gray-500">No built-in drill templates match this sport yet.</p>
                ) : (
                  <div className="grid gap-3">{groupedDrills.sas.map(renderDrillCard)}</div>
                )}
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">My Drills</p>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">{groupedDrills.mine.length}</span>
                </div>
                {loadingDrillTemplates ? (
                  <p className="text-sm text-gray-500">Loading custom drill templates...</p>
                ) : groupedDrills.mine.length === 0 ? (
                  <p className="text-sm text-gray-500">No personal drill templates match these filters yet.</p>
                ) : (
                  <div className="grid gap-3">{groupedDrills.mine.map(renderDrillCard)}</div>
                )}
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">School Drills</p>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">{groupedDrills.school.length}</span>
                </div>
                {loadingDrillTemplates ? (
                  <p className="text-sm text-gray-500">Loading school drill templates...</p>
                ) : groupedDrills.school.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-gray-500">
                    {organizationId
                      ? 'No org-shared drill templates match these filters yet for this instructor.'
                      : 'Ask an admin to assign orgId / orgName / orgRole on this instructor profile to unlock school-shared drill libraries.'}
                  </p>
                ) : (
                  <div className="grid gap-3">{groupedDrills.school.map(renderDrillCard)}</div>
                )}
              </div>
            </div>

            <details className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <summary className="cursor-pointer list-none text-sm font-semibold text-slate-900">
                Create Custom Drill Template
                <span className="ml-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  {organizationId ? 'Private or org-shared' : 'Private only until orgId is added'}
                </span>
              </summary>

              <form onSubmit={handleCreateCustomDrill} className="mt-4 space-y-3">
                <input
                  type="text"
                  value={customDrillForm.title}
                  onChange={(event) => handleCustomDrillFormChange('title', event.target.value)}
                  placeholder="Drill title"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <div className="grid gap-3 md:grid-cols-2">
                  <input
                    type="text"
                    value={customDrillForm.discipline}
                    onChange={(event) => handleCustomDrillFormChange('discipline', event.target.value)}
                    placeholder="Discipline (for example all-mountain)"
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                  />
                  <select
                    value={customDrillForm.level}
                    onChange={(event) => handleCustomDrillFormChange('level', event.target.value)}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                  >
                    <option value="all">All levels</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <input
                  type="text"
                  value={customDrillForm.topic}
                  onChange={(event) => handleCustomDrillFormChange('topic', event.target.value)}
                  placeholder="Topic or movement theme"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                {availableSkills.length > 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Related Skills</p>
                        <p className="mt-1 text-sm text-slate-600">
                          Optional SAS ski skill links for this drill. Keep it focused to up to 2 skills.
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">
                        {customDrillRelatedSkills.length}/2 linked
                      </span>
                    </div>

                    {customDrillRelatedSkills.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {customDrillRelatedSkills.map((skill) => (
                          <button
                            key={skill.skillId}
                            type="button"
                            onClick={() => handleToggleCustomDrillSkill(skill)}
                            className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
                          >
                            {skill.name} ×
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 space-y-3">
                      {Object.entries(groupedAvailableSkills).map(([groupName, skills]) => (
                        <div key={`custom-${groupName}`}>
                          <p className="mb-2 text-sm font-semibold text-slate-900 capitalize">{groupName}</p>
                          <div className="flex flex-wrap gap-2">
                            {skills.map((skill) => {
                              const selected = customDrillRelatedSkills.some((item) => item.skillId === skill.skillId);
                              return (
                                <button
                                  key={skill.skillId}
                                  type="button"
                                  onClick={() => handleToggleCustomDrillSkill(skill)}
                                  disabled={!selected && customDrillRelatedSkills.length >= 2}
                                  className={`rounded-full border px-3 py-2 text-sm ${
                                    selected
                                      ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                      : 'border-slate-300 bg-white text-slate-700'
                                  } disabled:cursor-not-allowed disabled:opacity-50`}
                                >
                                  {skill.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <input
                  type="text"
                  value={customDrillForm.commonError}
                  onChange={(event) => handleCustomDrillFormChange('commonError', event.target.value)}
                  placeholder="Common error this drill addresses"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <input
                  type="text"
                  value={customDrillForm.correctionCue}
                  onChange={(event) => handleCustomDrillFormChange('correctionCue', event.target.value)}
                  placeholder="Correction cue"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <textarea
                  value={customDrillForm.description}
                  onChange={(event) => handleCustomDrillFormChange('description', event.target.value)}
                  placeholder="Describe the drill and what it should improve"
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <input
                  type="text"
                  value={customDrillForm.focusAreasText}
                  onChange={(event) => handleCustomDrillFormChange('focusAreasText', event.target.value)}
                  placeholder="Focus areas (comma separated)"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <input
                  type="text"
                  value={customDrillForm.tagsText}
                  onChange={(event) => handleCustomDrillFormChange('tagsText', event.target.value)}
                  placeholder="Tags (comma separated)"
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />
                <textarea
                  value={customDrillForm.stepsText}
                  onChange={(event) => handleCustomDrillFormChange('stepsText', event.target.value)}
                  placeholder="One drill step per line"
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                />

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Visibility</label>
                  <select
                    value={customDrillForm.visibility}
                    onChange={(event) => handleCustomDrillFormChange('visibility', event.target.value)}
                    disabled={!organizationId}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                  >
                    <option value="private">Private to me</option>
                    <option value="org">Share with my school instructors</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={savingCustomDrill}
                  className="rounded-2xl border border-slate-800 bg-slate-800 px-4 py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingCustomDrill ? 'Saving drill...' : 'Save Custom Drill Template'}
                </button>

                {customDrillMessage && (
                  <p className="text-sm text-gray-600">{customDrillMessage}</p>
                )}
              </form>
            </details>
          </section>

          <section className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm md:p-5">
            <div className="mb-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Final Step</p>
              <h4 className="mt-2 text-lg font-semibold text-emerald-950">Complete Review</h4>
              <p className="mt-1 text-sm text-emerald-900">
                Mark the review complete only after your timestamped notes, optional audio, AI draft decisions, and drills are ready.
              </p>
            </div>
            <button
              onClick={markAsComplete}
              type="button"
              disabled={audioUploading}
              className="w-full rounded-2xl bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700"
            >
              {audioUploading ? 'Finish audio upload first' : '✅ Mark Feedback as Complete'}
            </button>

            {feedbackComplete && (
              <p className="mt-3 text-center text-green-700">
                ✅ Feedback submitted successfully! Redirecting...
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default FeedbackTimelineEditor;
